<template>
  <div v-if="visible" class="config-dialog">
    <div class="dialog-content">
      <div class="dialog-header-inner">
        <div class="small-title">
          <span>选择要显示的逻辑点</span>
        </div>
      </div>
      <div v-if="hasDialogPermission" class="dialog-body-content">
        <div class="selection-actions">
          <el-checkbox
            :value="isAllSelected"
            :indeterminate="isIndeterminate"
            @change="toggleSelectAll"
          >
            全选
          </el-checkbox>
          <!-- 搜索框（2026-10-13）：紧挨「全选」右侧 20px（不靠最右侧），
               只过滤本页下方的按钮列表（按按钮名称模糊匹配）；
               输入即过滤、清空即恢复全部；只影响展示，不影响选中态与请求参数。 -->
          <el-input
            v-model="searchKeyword"
            class="option-search"
            style="width:150px;margin-left:20px;"
            size="mini"
            clearable
            :placeholder="searchPlaceholder"
          />
        </div>
        <div class="options-container">
          <div class="option-item" v-for="point in visibleLogicPoints" :key="point.id">
            <button
              type="button"
              class="option-button"
              :class="{ 'active': point.selected }"
              @click="togglePointSelected(point)"
            >
              <span class="option-label">{{ point.name }}</span>
              <!-- 按钮高亮 = 「参与请求」态（point.selected，与恒星/行星同一口径）：
                     - 点按钮选中=参与+展示；点按钮取消=不再展示；
                       取消只影响画布展示，**不会**把它移出后端请求参数（参与集合只增不减）。
                   右侧确认框只控制「画布展示」（取消勾选=透明隐藏，仍在请求里）。
                   例外：点「全选」会把全部逻辑点并入请求；点「取消全选」可把请求收回。 -->
              <el-checkbox
                class="option-canvas-check"
                :value="point.visibleOnCanvas === true"
                @click.native.stop
                @change="val => toggleCanvasVisible(point, val)"
              />
            </button>
          </div>
          <!-- 搜索无结果时的空态（本页只有一个列表，不存在页签配对问题） -->
          <div
            v-if="visibleLogicPoints.length === 0"
            style="padding:12px 4px;font-size:12px;color:#909399;"
          >
            没有匹配「{{ searchKeyword }}」的逻辑点，请更换关键字
          </div>
        </div>
      </div>
      <div class="dialog-footer">
        <div class="dialog-buttons">
          <button class="btn-cancel" @click="cancel">
            取消
          </button>
          <button class="btn-confirm" @click="confirm">
            确定
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PermissionService from '@/services/PermissionService'
// 「参与请求」集合（只增不减）：取消只影响画布展示，不影响请求参数与报警判定（2026-10-10）
const canvasParticipation = require('../../features/tianxing/utils/canvasParticipation')

// 按钮名称搜索过滤（只按名称子串匹配）
const nameSearchFilter = require('../../utils/nameSearchFilter')

export default {
  name: 'LogicPointConfigDialog',
  props: {
    visible: Boolean,
    logicPoints: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      // 「参与请求」集合（2026-10-10，**只增不减**）：
      // 任何"取消"动作都不会把逻辑点从请求里移除，因此被隐藏的逻辑点
      // 数据照常运行、照常参与报警判定。
      participationIds: [],
      // 按钮名称搜索（2026-10-13）：只过滤「展示」，不影响选中态/参与集合/请求参数
      searchKeyword: ''
    }
  },
  computed: {
    // 「全选」勾选态 = 「参与请求」态（与按钮同一口径，2026-10-10 还原）
    isAllSelected () {
      return this.logicPoints.length > 0 && this.logicPoints.every(p => p.selected)
    },
    isIndeterminate () {
      const selectedCount = this.logicPoints.filter(p => p.selected).length
      return selectedCount > 0 && selectedCount < this.logicPoints.length
    },
    hasDialogPermission () {
      return PermissionService.hasPermission('logic_points')
    },
    // ---- 按钮名称搜索（2026-10-13）----
    // 只过滤「展示列表」；选中态、参与集合、请求参数一律不受影响。
    // 关键字为空即原样返回全部（"没有查询就恢复全部内容"）。
    visibleLogicPoints () {
      return nameSearchFilter.filterOptions(this.logicPoints, this.searchKeyword)
    },
    searchPlaceholder () {
      return '搜索逻辑点名称'
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (val) {
        if (val) {
          this.seedParticipation(this.logicPoints)
          // 打开弹窗清空关键字，避免上次过滤条件残留（否则会"一打开就少了一半"）
          this.searchKeyword = ''
        }
      }
    },
    logicPoints: {
      immediate: true,
      handler (list) {
        this.seedParticipation(list)
      }
    }
  },
  methods: {
    // 全选 / 取消全选（2026-10-10）：
    //   全选     → 全部并入「参与请求」集合（用户主动要全部）；
    //   取消全选 → 清空参与集合，把请求收回（用于清除历史上被"全选"放大的请求参数）。
    toggleSelectAll (val) {
      this.logicPoints.forEach(point => {
        point.selected = val
        // 与单点按钮同一口径：全选即全部勾选画布展示，全不选即全部取消勾选
        this.$set(point, 'visibleOnCanvas', val === true)
      })
      this.participationIds = val === true ? this.logicPoints.map(point => point.id) : []
    },
    // 逻辑点按钮点击：切换选中状态，并与「画布展示」确认框联动（2026-10-09 调整）。
    //   选中按钮   → 连带勾选确认框（与历史行为一致）；
    //   取消按钮   → 连带取消勾选确认框（本次新增）。
    // 反向约定：勾选确认框会连带选中按钮；取消勾选确认框不影响按钮。
    // 与行星/恒星配置保持同一口径。
    // 2026-10-10：无论选中还是取消，都只影响「画布展示」；已出现过的逻辑点恒留在请求里。
    togglePointSelected (point) {
      if (!point) return
      point.selected = !point.selected
      this.$set(point, 'visibleOnCanvas', point.selected === true)
      if (point.selected === true) {
        this.markParticipation(point.id)
      }
    },
    // 画布展示确认框：勾选=画布绘制该逻辑点，取消勾选=停止绘制。
    //   勾选   → 连带把该逻辑点按钮选上（配合"要展示就得先选中"的口径）；
    //   取消勾选 → 只改画布展示状态，不修改 point.selected，也不触发其它状态变更。
    // 2026-10-10：取消勾选**不会**把逻辑点移出请求，数据照常运行、照常参与报警判定。
    toggleCanvasVisible (point, visible) {
      if (!point) return
      this.$set(point, 'visibleOnCanvas', visible === true)
      if (visible === true) {
        point.selected = true
        // 只有"要展示"才需要并入请求（展示依赖后端返回的数据）；
        // 取消勾选只是停止绘制，既不移出请求、也不新增请求项
        this.markParticipation(point.id)
      }
    },
    // 记录「参与请求」的对象 id（**只增不减**，永不删除）
    markParticipation (id) {
      this.participationIds = canvasParticipation.markParticipation(this.participationIds, id)
    },
    // 把列表里"当前已选中"的对象并入参与集合（并集，不做删除）
    seedParticipation (list) {
      this.participationIds = canvasParticipation.seedParticipation(this.participationIds, list)
    },
    // 提交给宿主的请求对象列表 = 参与集合 ∪ 当前选中（保证"取消"不会减少请求参数）
    resolveRequestIds () {
      return canvasParticipation.resolveRequestIds(this.participationIds, this.logicPoints)
    },
    confirm () {
      if (!this.hasDialogPermission) {
        this.$emit('update:visible', false)
        return
      }

      // 「参与请求」只增不减（2026-10-10）：提交"参与集合 ∪ 当前选中"，
      // 保证取消按钮 / 取消勾选确认框只影响画布展示，不影响请求参数与报警判定。
      const selectedIds = this.resolveRequestIds()
      this.$emit('save', selectedIds)

      // 画布展示：把「确认框未勾选」的逻辑点 id 告知画布。
      // 画布只据此停止绘制这些逻辑点，选中列表（selectedIds）保持原样，
      // 因此请求参数等其它方面都不受影响。
      const canvasHiddenIds = this.logicPoints
        .filter(p => p.visibleOnCanvas !== true)
        .map(p => p.id)
      this.$emit('update-canvas-visible', canvasHiddenIds)

      this.$emit('update:visible', false)
    },
    cancel () {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.dialog-content {
  width: 800px;
  height: 600px;
  background: white;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
}

.dialog-header-inner {
  background: #f0f4f8;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 24px;
}

.small-title {
  font-size: 1.1rem;
  font-weight: 500;
  color: #4a5568;
  padding: 4px 0;
}

.dialog-body-content {
  padding: 10px 24px 20px;
  flex: 1;
  overflow: auto;
  display: flex;
  flex-direction: column;
}

.selection-actions {
  padding: 0 0 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #edf2f7;
  display: flex;
  align-items: center;
}

.selection-actions >>> .el-checkbox__label {
  font-size: 0.9rem;
  color: #4a5568;
}

.options-container {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

.option-item {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 按钮基础样式 - 尺寸更小 */
.option-button {
  width: 100%;
  min-height: 32px;
  padding: 4px 8px;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  background: #f7fafc;
  color: #4a5568;
  font-size: 0.8rem;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  word-break: break-word;
  white-space: normal;
}

/* 未选中状态 - 灰色 */
.option-button:not(.active) {
  background: #f7fafc;
  color: #718096;
  border-color: #cbd5e0;
}

/* 选中状态 - 高亮蓝色 */
.option-button.active {
  background: #3182ce;
  color: white;
  border-color: #3182ce;
  box-shadow: 0 1px 3px rgba(49, 130, 206, 0.3);
}

/* 悬停效果 */
.option-button:hover:not(.active) {
  background: #edf2f7;
  border-color: #a0aec0;
}

.option-button.active:hover {
  background: #2c5aa0;
  border-color: #2c5aa0;
}

/* 按钮内文字：占满剩余宽度并居中；右侧固定放「画布展示」确认框 */
.option-label {
  flex: 1;
  text-align: center;
  padding-left: 16px; /* 左侧补偿确认框宽度，保持文字视觉居中 */
}

/* 按钮内的画布展示确认框 */
.option-canvas-check {
  margin-right: 6px;
  flex-shrink: 0;
}

/* 蓝色选中态下确认框用白底蓝勾，保证在蓝底上可见 */
.option-button.active .option-canvas-check >>> .el-checkbox__inner {
  background-color: #ffffff;
  border-color: #ffffff;
}

.option-button.active .option-canvas-check >>> .el-checkbox__inner::after {
  border-color: #3182ce;
}

.permission-guard {
  margin: 18px 24px;
  padding: 16px 18px;
  border-radius: 8px;
  background: #fff7e6;
  border: 1px solid #f5c98a;
  color: #8a5a12;
  font-size: 14px;
}

.dialog-footer {
  padding: 8px 24px;
  background: #f8f9fa;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

.dialog-buttons {
  display: flex;
  gap: 12px;
}

.btn-confirm, .btn-cancel {
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
}

.btn-confirm {
  background: #3182ce;
  color: white;
  border: 1px solid #3182ce;
}

.btn-cancel {
  background: #f7fafc;
  color: #4a5568;
  border: 1px solid #cbd5e0;
}

@media (max-width: 850px) {
  .options-container {
    grid-template-columns: repeat(4, 1fr);
  }

  .dialog-content {
    width: 95%;
    height: 85vh;
  }
}

@media (max-width: 600px) {
  .options-container {
    grid-template-columns: repeat(3, 1fr);
  }

  .option-button {
    min-height: 28px;
    font-size: 0.75rem;
    padding: 3px 6px;
  }
}
</style>
